import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Save, Upload, Eye, Loader2, FileText } from "lucide-react";
import { adminGetLegal, adminUpdateLegal, formatApiError } from "../../lib/adminApi";
import { sanitizeHtml } from "../../lib/sanitize";

const SLUG = "dpa";

function formatDanishDate(iso) {
    if (!iso) return null;
    try {
        return new Date(iso).toLocaleString("da-DK", {
            day: "numeric",
            month: "long",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
}

export default function AdminLegalDpaPage() {
    const fileInputRef = useRef(null);
    const [doc, setDoc] = useState(null);
    const [title, setTitle] = useState("");
    const [content, setContent] = useState("");
    const [previewHtml, setPreviewHtml] = useState("");
    const [showPreview, setShowPreview] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        adminGetLegal(SLUG)
            .then((d) => {
                setDoc(d);
                setTitle(d.title);
                setContent(d.content);
                setPreviewHtml(d.html);
            })
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
    }, []);

    const handleFile = async (file) => {
        if (!file) return;
        if (!/\.(md|markdown|txt)$/i.test(file.name)) {
            toast.error("Kun .md, .markdown og .txt filer er understøttet");
            return;
        }
        try {
            const text = await file.text();
            setContent(text);
            toast.success(`Indlæst ${file.name} (${(file.size / 1024).toFixed(1)} KB)`);
        } catch (e) {
            toast.error("Kunne ikke læse fil");
        }
    };

    const handleSave = async () => {
        if (!title.trim() || content.trim().length < 10) {
            toast.error("Titel og indhold er påkrævet");
            return;
        }
        setSaving(true);
        try {
            const updated = await adminUpdateLegal(SLUG, { title: title.trim(), content });
            setDoc(updated);
            setPreviewHtml(updated.html);
            toast.success(`Gemt — nu version ${updated.version}`);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center gap-2 text-slate-500 text-sm">
                <Loader2 className="h-4 w-4 animate-spin" /> Henter…
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="admin-legal-dpa">
            <header>
                <div className="text-xs uppercase tracking-wider font-semibold text-sky-700">Juridisk</div>
                <h1 className="mt-2 text-3xl font-display font-bold text-slate-900">Databehandleraftale</h1>
                <p className="mt-2 text-sm text-slate-600">
                    Rediger eller upload en ny version af databehandleraftalen. Indholdet er Markdown og
                    vises på <code className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">/databehandleraftale</code>{" "}
                    på det offentlige website.
                </p>
                {doc && (
                    <div className="mt-4 inline-flex items-center gap-3 rounded-lg bg-sky-50 border border-sky-200 px-3 py-2 text-sm">
                        <FileText className="h-4 w-4 text-sky-700" />
                        <span className="text-slate-700">
                            Nuværende version: <strong>v{doc.version}</strong> · sidst opdateret{" "}
                            <strong>{formatDanishDate(doc.updated_at)}</strong>
                        </span>
                    </div>
                )}
            </header>

            <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
                <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-3">
                    <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="Titel"
                        data-testid="admin-dpa-title"
                        className="flex-1 text-lg font-semibold bg-transparent focus:outline-none placeholder:text-slate-300"
                    />
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".md,.markdown,.txt"
                        onChange={(e) => handleFile(e.target.files?.[0])}
                        className="hidden"
                        data-testid="admin-dpa-file-input"
                    />
                    <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        data-testid="admin-dpa-upload-btn"
                        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 hover:border-sky-300 hover:bg-sky-50 hover:text-sky-700 px-3 h-9 text-sm font-semibold text-slate-700 transition-colors"
                    >
                        <Upload className="h-4 w-4" />
                        Upload fil (.md / .txt)
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            setShowPreview((v) => !v);
                            // Lightweight preview using last-saved HTML or local naive renderer? We re-fetch on save.
                        }}
                        data-testid="admin-dpa-preview-toggle"
                        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 hover:border-slate-300 px-3 h-9 text-sm font-semibold text-slate-700 transition-colors"
                    >
                        <Eye className="h-4 w-4" />
                        {showPreview ? "Skjul preview" : "Forhåndsvis"}
                    </button>
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={saving}
                        data-testid="admin-dpa-save-btn"
                        className="inline-flex items-center gap-1.5 rounded-lg bg-sky-600 hover:bg-sky-700 text-white px-4 h-9 text-sm font-semibold shadow-sm disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                    >
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        {saving ? "Gemmer…" : "Gem ny version"}
                    </button>
                </div>

                {showPreview ? (
                    <div
                        className="prose-cleany p-6 min-h-[400px] max-h-[70vh] overflow-y-auto"
                        data-testid="admin-dpa-preview"
                        dangerouslySetInnerHTML={{
                            __html: sanitizeHtml(previewHtml) ||
                                "<p class='text-slate-400'>Gem først for at se renderet preview…</p>",
                        }}
                    />
                ) : (
                    <textarea
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        placeholder="Markdown-indhold for databehandleraftalen…"
                        data-testid="admin-dpa-content"
                        className="w-full p-6 min-h-[400px] resize-y bg-white text-sm font-mono leading-relaxed focus:outline-none focus:ring-0 border-0"
                        spellCheck={false}
                    />
                )}
            </div>

            <p className="text-xs text-slate-500">
                Tip: Du kan uploade en hel ny aftale ved at klikke <em>Upload fil</em> og vælge en{" "}
                <code className="px-1 py-0.5 rounded bg-slate-100">.md</code> eller{" "}
                <code className="px-1 py-0.5 rounded bg-slate-100">.txt</code> fil. Hver gang du gemmer
                bumpes version-nummeret, og datoen vises automatisk på den offentlige side.
            </p>
        </div>
    );
}
